import {
  HttpClientModule,
  HTTP_INTERCEPTORS,
  HttpClient,
} from "@angular/common/http";
import {
  APP_INITIALIZER,
  LOCALE_ID,
  NgModule,
  ErrorHandler,
} from "@angular/core";
import { MatGridListModule } from "@angular/material/grid-list";
import { BrowserModule, HAMMER_LOADER } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule } from "@angular/router";
import ptBr from "@angular/common/locales/pt";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AuthGuard } from "./core/auth/auth.guard";
import { CoreModule } from "./core/core.module";
import { EnvironmentService } from "./core/environment/environment.service";
import { FullMenuModule } from "./full-menu/full-menu.module";
import { HomeComponent } from "./home/home.component";
import { PageGuideComponent } from "./page-guide/page-guide.component";
import { OccurencesComponent } from "./occurences/occurences.component";
import { SearchModule } from "./search/search.module";
import { ErrorComponent } from "./shared/error/error.component";
import { SharedModule } from "./shared/shared.module";
import { HashLocationStrategy, LocationStrategy } from "@angular/common";
import {
  MatDialogModule,
  MatTableModule,
  MatPaginatorModule,
  MatSortModule,
  MatTooltipModule,
  MatToolbarModule,
  MAT_DATE_LOCALE,
} from "@angular/material";
import { AddOccurenceModalComponent } from "./occurences/add-occurence-modal/add-occurence-modal.component";
import { AddOccurenceModalModule } from "./occurences/add-occurence-modal/add-occurence-modal.module";
import { ReactiveFormsModule } from "@angular/forms";
import { SnackbarService } from "./shared/services/snackbar.service";
import { NotFoundRouteComponent } from "./not-found-route/not-found-route.component";
import { AppOverlayModule } from "./shared/loading/overlay.module";
import { AuthInterceptor } from "./http-interceptor";
import { BrowserWarningComponent } from "./browser-warning/browser-warning.component";
import { TextMaskModule } from "angular2-text-mask";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { BrokerageFeeComponent } from "./brokerage-fee/brokerage-fee.component";
import { DialogBrookageComponent } from "./brokerage-fee/dialog-brookage/dialog-brookage.component";
import { registerLocaleData } from "@angular/common";
import { DatadogService } from "./shared/services/datadog/datadog.service";
import { SdkAnalyticsService } from './shared/services/sdk-analytics/sdk-analytics.service';
import { GlobalErrorHandler } from "./shared/handlers/global-error.handler";
import { ANALYTICS_CONFIG, AnalyticsConfig } from './shared/interface/sdk-analytics';


export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    "./assets/i18n/", // or whatever path you're using
    ".json"
  );
}

// Fallback estático
const analyticsConfig: AnalyticsConfig = {
  sdkUrl: 'https://sdk-analytics-web.cloud.itau.com.br/index.js',
  gtmId: 'GTM-MKWS5TC',//'GTM-NFX5HTWX' (dev/hom), //'GTM-MKWS5TC' (prod)
  defaultParameters: {
    business: 'investimentos-app-e-web',
    business_channel: 'portal-is-ativos',
    environment: 'logado'
  },
  timeoutMs: 1500
};

registerLocaleData(ptBr);

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    ErrorComponent,
    PageGuideComponent,
    OccurencesComponent,
    NotFoundRouteComponent,
    BrowserWarningComponent,
    BrokerageFeeComponent,
    DialogBrookageComponent,
  ],
  imports: [
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    BrowserModule,
    SearchModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    CoreModule,
    HttpClientModule,
    MatGridListModule,

    SharedModule,
    FullMenuModule,
    RouterModule,
    AddOccurenceModalModule,
    MatDialogModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    ReactiveFormsModule,
    AppOverlayModule,
    MatTooltipModule,
    TextMaskModule,
    MatToolbarModule,
  ],

  providers: [
    AuthGuard,
    { provide: "Window", useValue: window },
    { provide: ANALYTICS_CONFIG, useValue: analyticsConfig },
    {
      provide: APP_INITIALIZER,
      useFactory: (env: EnvironmentService, sdk: SdkAnalyticsService, datadogService: DatadogService) => () => {
        return env.loadEnvironment().toPromise().then((res: any) => {
          const envAnalytics = res && res.analyticsSettings ? res.analyticsSettings : null;
          const effectiveConfig: AnalyticsConfig = {
            sdkUrl: envAnalytics && envAnalytics.sdkUrl ? envAnalytics.sdkUrl : analyticsConfig.sdkUrl,
            gtmId: envAnalytics && envAnalytics.gtmId ? envAnalytics.gtmId : analyticsConfig.gtmId,
            defaultParameters: envAnalytics && envAnalytics.defaultParameters ? envAnalytics.defaultParameters : analyticsConfig.defaultParameters,
            timeoutMs: envAnalytics && envAnalytics.timeoutMs !== undefined ? envAnalytics.timeoutMs : analyticsConfig.timeoutMs
          };
          return sdk.loadSdk(
            effectiveConfig.gtmId,
            effectiveConfig.sdkUrl,
            effectiveConfig.defaultParameters
          ).then(() => {
            datadogService.configureDataDog();
          }).catch(() => { /* swallow para não bloquear o app */ });
        });
      },
      deps: [EnvironmentService, SdkAnalyticsService, DatadogService],
      multi: true,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: (service: EnvironmentService) => () => service.loadEnvironment().toPromise(),
      deps: [EnvironmentService],
      multi: true,
    },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HAMMER_LOADER, useValue: () => new Promise(() => { }) },
    { provide: LOCALE_ID, useValue: "pt" },
    SnackbarService,
  ],
  bootstrap: [AppComponent],
  entryComponents: [AddOccurenceModalComponent, DialogBrookageComponent],
})
export class AppModule {
}
